import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskText, Magnetic } from "./primitives";

const EASE = [0.16, 1, 0.3, 1];

export default function Hero({ onCta }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["0%", "26%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const yText = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden grain bg-dm-dark"
    >
      {/* Parallax image layer */}
      <motion.div className="absolute inset-0 z-0" style={{ y: yImg, scale: scaleImg }}>
        <img
          src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?q=80&w=2000&auto=format&fit=crop"
          alt="Doctor Media studio"
          className="h-full w-full object-cover opacity-40"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,10,10,0.55) 0%, rgba(10,10,10,0.75) 45%, rgba(10,10,10,0.97) 100%)",
          }}
        />
      </motion.div>

      {/* Floating brand orbs */}
      <motion.div
        aria-hidden
        className="absolute -left-24 top-1/3 z-0 h-72 w-72 rounded-full blur-[90px]"
        style={{ background: "var(--dm-teal)", opacity: 0.35 }}
        animate={{ y: [0, 30, 0] }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="absolute right-0 top-1/4 z-0 h-64 w-64 rounded-full blur-[100px]"
        style={{ background: "var(--dm-maroon)", opacity: 0.4 }}
        animate={{ y: [0, -34, 0] }}
        transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
      />

      <motion.div
        style={{ y: yText, opacity }}
        className="relative z-10 mx-auto max-w-[1500px] px-6 md:px-12 pt-40 md:pt-48 pb-16"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.3 }}
          className="flex items-center gap-3 text-white/70"
        >
          <span className="overline text-[11px]">Marketing &amp; Design Studio</span>
          <span className="h-px w-10 bg-white/30" />
          <span className="overline text-[11px]" style={{ color: "var(--dm-maroonbright)" }}>
            Iraq · UAE · Jordan
          </span>
        </motion.div>

        <div className="mt-8 md:mt-10">
          <MaskText
            as="h1"
            lines={["We make brands", "impossible"]}
            className="font-display font-extrabold text-white tracking-tighter leading-[0.92] text-6xl sm:text-7xl md:text-8xl lg:text-[8.5rem]"
            delay={0.35}
          />
          <div className="overflow-hidden">
            <motion.h1
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.62 }}
              className="font-display font-extrabold tracking-tighter leading-[0.92] text-6xl sm:text-7xl md:text-8xl lg:text-[8.5rem]"
              style={{ color: "var(--dm-maroonbright)" }}
            >
              to ignore<span className="text-white">.</span>
            </motion.h1>
          </div>
        </div>

        <div className="mt-10 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.8 }}
            className="max-w-md text-white/70 text-base md:text-lg leading-relaxed"
          >
            A full-service creative studio building strategy, identity and campaigns
            that move people — across the Middle East and beyond.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.9 }}
            className="flex flex-wrap items-center gap-4"
          >
            <Magnetic strength={0.5}>
              <button
                onClick={onCta}
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-2 rounded-full px-7 py-4 font-oswald uppercase tracking-[0.12em] text-sm text-white"
                style={{ background: "var(--dm-teal)" }}
              >
                Start a project
                <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </Magnetic>
            <a
              href="#work"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 rounded-full px-7 py-4 font-oswald uppercase tracking-[0.12em] text-sm text-white border border-white/25 hover:border-white/60 transition-colors duration-300"
            >
              View our work
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/50"
      >
        <span className="overline text-[10px]">Scroll</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.6, repeat: Infinity }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.div>
    </section>
  );
}
